import React, { CSSProperties, FC, useState } from 'react'
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs'
import { Button } from '@progress/kendo-react-buttons'
import {
  Grid,
  GridColumn,
  GridToolbar,
  GridCellProps
} from '@progress/kendo-react-grid'
import { Label } from '@progress/kendo-react-labels'
import { Popup } from '@progress/kendo-react-popup'
import { Ripple } from '@progress/kendo-react-ripple'

import { ReferenceCommandCell } from './ReferenceCommandCell'
import './SupplierSelect.css'
import { SearchSupplierListItem } from '@gateway.Supplier'
import { SearchSupplierGateway } from '../../gateway/supplier/SearchSupplierGateway'
import { ErrorMessage, ErrorMessages } from './ErrorMessages'
import { MessageProvider } from '../../services'
export interface SupplierSelectProps {
  searchFromXgrab?: boolean
  expense?: string
  isBtnDisabled?: boolean
  buttonOnly?: boolean
  buttonStyle?: CSSProperties
  onMenuClicked?: React.MouseEventHandler
  supplierSearchListItem?: (val: SearchSupplierListItem) => void
  handleSelectVal?: (val: SearchSupplierListItem) => void
  supplierNm?: string
  address?: string
  supplierCd?: string
  isSearchBtnDisabled?: boolean
  isInputDisabled?: boolean
}

export const SupplierSelect: FC<SupplierSelectProps> = ({
  searchFromXgrab = false,
  expense,
  isBtnDisabled,
  buttonOnly = false,
  buttonStyle,
  supplierNm,
  onMenuClicked,
  supplierSearchListItem,
  handleSelectVal,
  isSearchBtnDisabled = false,
  isInputDisabled = false
}) => {
  const intePageState = {
    skip: 0,
    take: 10
  }

  const refAnchor = React.useRef<HTMLDivElement>(null)
  const [showRefSearch, setShowRefSearch] = React.useState<boolean>(false)
  const [page, setPage] = React.useState(intePageState)

  const [supplierItems, setSupplierItems] = React.useState<SearchSupplierListItem[]>([])
  const [supplierNmInput, setSupplierNmInput] = React.useState(supplierNm)

  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])

  const CommandCell: FC<GridCellProps> = ({ dataItem }) => {
    const handleSelectVal = () => {
      handleCountSelectItem(dataItem)
      setShowRefSearch(false)
    }
    const data = {
      code: dataItem.supplierCd,
      name: dataItem.supplierNm
    }
    return <ReferenceCommandCell data={data} onClick={handleSelectVal} />
  }
  /**
   * ページをめくって押さえる
   */
  const pageChange = (event: {
    page: React.SetStateAction<{ skip: number, take: number }>
  }) => {
    setPage(event.page)
  }
  /**
   * 業者検索画面を開けたかどうか
   */
  const toggleRefSearch = () => {
    setShowRefSearch(!showRefSearch)
    if (!showRefSearch) {
      if (!searchFromXgrab) {
        getSearchApi('toggleRefSearch')
      } else {
        setSupplierItems([])
      }
    }
  }
  /**
   * 業者検索画面を閉じる
   */
  const closeRefSearch = () => {
    setShowRefSearch(false)
  }
  /**
   * 業者名検索値を取得する
   */
  const handleInputBlur = (e: {
    target: { value: React.SetStateAction<string | undefined> }
  }) => {
    setSupplierNmInput(e.target.value)
  }
  /**
   * 検索ボタン押下
   */
  const handleClickSearchBtn = () => {
    setPage({ skip: 0, take: 10 })
    if (searchFromXgrab && (supplierNmInput === undefined || supplierNmInput.length === 0)) {
      setErrorMessages([
        {
          id: 'E002',
          content: MessageProvider.provide('E002', '業者名')
        }
      ])
      return
    }
    getSearchApi('SearchBtn')
  }
  /**
   * リンクボタンを押した値を取得
   */
  const handleCountSelectItem = (supplierItem: GridCellProps['dataItem']) => {
    handleSelectVal?.(supplierItem)

    setSupplierNmInput(supplierItem.supplierNm)
    const supplierNmAndSupplierCd = {
      supplierNm: supplierItem.supplierNm,
      supplierCd: supplierItem.supplierCd,
      supplierBranchCd: null,
      supplierBranchNm: null,
      address: supplierItem.address,
      billCode: supplierItem.billCode,
      supplierPerson: supplierItem.supplierPerson
    }
    supplierSearchListItem?.(supplierNmAndSupplierCd)
  }
  const handleInputVal = (event: InputChangeEvent) => {
    setSupplierNmInput(event.value)
    const supplierNmAndSupplierCd = {
      supplierNm: event.value,
      supplierCd: '',
      supplierBranchCd: null,
      supplierBranchNm: null,
      address: '',
      billCode: '',
      supplierPerson: ''
    }
    supplierSearchListItem?.(supplierNmAndSupplierCd)
  }
  const getSearchApi = (type: string) => {
    if (searchFromXgrab) {
      SearchSupplierGateway.getSearchSupplierXgrabList(supplierNmInput)
        .then((res) => {
          if (!res.error) {
            const supplierList = res.data.supplierList
            setSupplierItems(supplierList)
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const errorMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(errorMessages)
            }
          }
        })
        .finally(() => {})
    } else {
      SearchSupplierGateway.getSearchSupplierList(
        type === 'toggleRefSearch' ? undefined : supplierNmInput,
        expense
      )
        .then((res) => {
          if (!res.error) {
            const supplierList = res.data.supplierList.map((supplier) => {
              return {
                ...supplier,
                supplierNm: supplier.supplierBranchNm === null ? `${supplier.supplierNm}` : `${supplier.supplierNm}  ${supplier.supplierBranchNm ?? ''}`
              }
            })
            setSupplierItems(supplierList)
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const errorMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(errorMessages)
            }
          }
        })
        .finally(() => {})
    }
  }
  return (
    <div style={{ width: '100%' }}>
      <div
        ref={refAnchor}
        style={{
          display: 'grid',
          gridTemplateColumns: buttonOnly ? 'auto' : '1fr auto'
        }}
      >
        {buttonOnly
          ? (
          <></>
            )
          : (
          <Input
            style={{ gridColumn: 1 }}
            value={supplierNmInput}
            onChange={handleInputVal}
            disabled={isInputDisabled}
          />
            )}
        <Button
          icon="search"
          style={buttonStyle}
          togglable
          selected={showRefSearch}
          onClick={toggleRefSearch}
          disabled={isSearchBtnDisabled || isBtnDisabled}
        />
      </div>
      <Popup
        className={'pcy-supplier-select-popup'}
        show={showRefSearch}
        anchor={refAnchor.current}
      >
        <Ripple>
          <Grid
            data={supplierItems?.slice(page.skip, page.take + page.skip)}
            skip={page.skip}
            take={page.take}
            total={supplierItems?.length}
            editField="inEdit"
            style={{ width: 800, height: 350 }}
            pageable
            onPageChange={pageChange}
          >
            <GridToolbar>
              <Label>業者名</Label>
              <Input required={searchFromXgrab} style={{ width: '70%' }} onBlur={handleInputBlur} />
              <Button
                className="pcy-button-short"
                themeColor="primary"
                onClick={handleClickSearchBtn}
              >
                検索
              </Button>
              <Button
                icon="close"
                themeColor="error"
                onClick={closeRefSearch}
              />
            </GridToolbar>
            <GridColumn title="業者名" cell={CommandCell} field="supplierNm" width={'275px'}/>
            <GridColumn title="住所" field="address" width={'275px'}/>
            <GridColumn title='支払先コード' field="billCode" />
            {!searchFromXgrab && (
              <GridColumn title='担当者' field="supplierPerson" />
            )}
          </Grid>
        </Ripple>
      </Popup>
      <ErrorMessages style={{ zIndex: '30001' }} type="dialog" messages={errorMessages} />
    </div>
  )
}
